import { useId, useMemo } from 'react';
import Select, {
    components,
    type ClassNamesConfig,
    type FilterOptionOption,
    type GroupBase,
    type MultiValue,
    type MultiValueGenericProps,
    type OptionProps,
    type SingleValue,
    type SingleValueProps
} from 'react-select';
import { FaCheck } from 'react-icons/fa';
import { formStyles } from '@utils/formStyles';

export type SearchSelectBadgeTone = 'warning' | 'success' | 'neutral' | 'danger';

export interface SearchSelectItem {
    value: number;
    /** Entity ID to display and search when the selection value is a local ordinal. */
    displayId?: number;
    /** Plain-text label used by screen readers and aria announcements */
    label: string;
    /** Row title and chip text */
    name: string;
    /** Optional row subtitle, e.g. "Out of the money · 30 DTE (21-45 range)" */
    meta?: string;
    /** Optional pill on the row and on the selected chip, e.g. "Inactive" */
    badge?: string;
    /** Pill color; defaults to the amber warning tone */
    badgeTone?: SearchSelectBadgeTone;
    /** Optional heading the option is listed under; items sharing a group render together in first-seen order */
    group?: string;
    /** Extra lowercase haystack for search, beyond name and id */
    searchText?: string;
    /** Whether a server-backed search included this option; false keeps hydration-only values out of the menu. */
    serverSearchMatch?: boolean;
    /** Renders the row dimmed and unselectable (react-select's default isOptionDisabled accessor) */
    isDisabled?: boolean;
}

/** Form-field props shared by SearchSelect and its domain wrappers */
export interface SearchSelectFormProps {
    label?: string;
    /** Accessible name for the control when there is no visible label */
    'aria-label'?: string;
    helpText?: string;
    error?: string;
    placeholder?: string;
    isDisabled?: boolean;
    size?: 'sm' | 'md';
    fullWidth?: boolean;
    noMargin?: boolean;
    className?: string;
    id?: string;
}

interface CommonProps extends SearchSelectFormProps {
    items: SearchSelectItem[];
    isLoading?: boolean;
    /** The owning component already fetched matching options from the server. */
    isServerFiltered?: boolean;
    /** Data-fetch failure message, shown below the control alongside any field error */
    loadError?: string;
    /** Receives the current search text. Domain wrappers can use this for server-backed search. */
    onSearchChange?: (value: string) => void;
    /** Reader-facing message shown when the current search has no matches. */
    noOptionsMessage?: string;
    /** Builds a display item for a selected value missing from items (e.g. archived) */
    resolveMissingItem?: (value: number) => SearchSelectItem;
}

interface SingleProps extends CommonProps {
    mode: 'single';
    value: number | null;
    onChange: (value: number | null) => void;
}

interface MultiProps extends CommonProps {
    mode: 'multi';
    value: number[];
    onChange: (values: number[]) => void;
}

export type SearchSelectProps = SingleProps | MultiProps;

const BADGE_TONE_CLASSES: Record<SearchSelectBadgeTone, string> = {
    warning: 'bg-amber-950/60 text-amber-400',
    success: 'bg-green-950/60 text-green-400',
    neutral: 'bg-zinc-800 text-zinc-400',
    danger: 'bg-red-950/60 text-red-400'
};

const badgeClasses = (tone: SearchSelectBadgeTone = 'warning') => BADGE_TONE_CLASSES[tone];

/** Groups items under their `group` heading when any item declares one; otherwise the flat list is used as-is. */
const groupItems = (items: SearchSelectItem[]): SearchSelectItem[] | GroupBase<SearchSelectItem>[] => {
    if (!items.some((item) => item.group)) return items;

    const groups = new Map<string, SearchSelectItem[]>();

    for (const item of items) {
        const label = item.group ?? 'Other';
        const group = groups.get(label);

        if (group) group.push(item);
        else groups.set(label, [item]);
    }

    return [...groups.entries()].map(([label, options]) => ({ label, options }));
};

const defaultMissingItem = (value: number): SearchSelectItem => ({
    label: `Item ${value} (#${value})`,
    name: `Item ${value}`,
    value
});

// Matches against the item name, id (with or without a leading #), and searchText
const filterOption = (candidate: FilterOptionOption<SearchSelectItem>, rawInput: string): boolean => {
    const input = rawInput.trim().toLowerCase().replace(/^#/, '');

    if (!input) return true;

    if (String(candidate.data.displayId ?? candidate.data.value).includes(input)) return true;
    if (candidate.data.name.toLowerCase().includes(input)) return true;

    return (candidate.data.searchText ?? '').includes(input);
};

const ItemOption = (props: OptionProps<SearchSelectItem, boolean, GroupBase<SearchSelectItem>>) => {
    const item = props.data;

    return (
        <components.Option {...props}>
            <div className="flex items-center gap-2">
                <span className="flex w-4 shrink-0 justify-center">
                    {props.isSelected && <FaCheck className="h-3 w-3 text-blue-400" />}
                </span>
                <span className="min-w-0 flex-1">
                    <span
                        className={`block truncate text-sm font-medium ${props.isSelected ? 'text-blue-300' : 'text-zinc-200'}`}
                        title={item.name}
                    >
                        {item.name}
                    </span>
                    {item.meta && (
                        <span className="block truncate text-xs text-zinc-500" title={item.meta}>
                            {item.meta}
                        </span>
                    )}
                </span>
                {item.badge && (
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${badgeClasses(item.badgeTone)}`}>
                        {item.badge}
                    </span>
                )}
                <span className="shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-xs text-zinc-400">
                    #{item.displayId ?? item.value}
                </span>
            </div>
        </components.Option>
    );
};

const ItemSingleValue = (props: SingleValueProps<SearchSelectItem, boolean, GroupBase<SearchSelectItem>>) => (
    <components.SingleValue {...props}>
        <span className="flex items-center gap-1.5">
            <span className="truncate" title={props.data.name}>
                {props.data.name}
            </span>
            <span className="shrink-0 font-mono text-xs text-zinc-500">#{props.data.displayId ?? props.data.value}</span>
        </span>
    </components.SingleValue>
);

const ItemMultiValueLabel = (props: MultiValueGenericProps<SearchSelectItem, boolean, GroupBase<SearchSelectItem>>) => {
    const item = props.data as SearchSelectItem;

    return (
        <components.MultiValueLabel {...props}>
            <span className="flex items-center gap-1">
                <span className="truncate" title={item.name}>
                    {item.name}
                </span>
                {item.badge && (
                    <span className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium ${badgeClasses(item.badgeTone)}`}>
                        {item.badge}
                    </span>
                )}
                <span className="shrink-0 font-mono text-[11px] text-zinc-500">#{item.displayId ?? item.value}</span>
            </span>
        </components.MultiValueLabel>
    );
};

// react-select's replaceable component keys are PascalCase by API design
const SELECT_COMPONENTS = {
    MultiValueLabel: ItemMultiValueLabel,
    Option: ItemOption,
    SingleValue: ItemSingleValue
};

const getContainerClasses = (fullWidth: boolean, noMargin: boolean): string => {
    if (noMargin) {
        return fullWidth ? formStyles.fullWidthContainerNoMargin : formStyles.containerNoMargin;
    }

    return fullWidth ? formStyles.fullWidthContainer : formStyles.container;
};

const buildClassNames = (
    hasError: boolean,
    size: 'sm' | 'md'
): ClassNamesConfig<SearchSelectItem, boolean, GroupBase<SearchSelectItem>> => ({
    clearIndicator: () => 'cursor-pointer text-zinc-500 hover:text-red-400',
    control: (state) => {
        const sizeClasses = size === 'sm' ? 'min-h-[32px] text-sm' : 'min-h-[38px]';

        const borderClasses = hasError ? 'border-red-500 ring-red-500' : state.isFocused ? 'border-[#888] ring-[#888]' : 'border-[#444]';

        const ringClasses = state.isFocused ? 'ring-1' : '';
        const disabledClasses = state.isDisabled ? 'cursor-not-allowed opacity-60' : '';

        return `rounded-md border bg-[#1E1E1E] transition-colors ${sizeClasses} ${borderClasses} ${ringClasses} ${disabledClasses}`;
    },
    dropdownIndicator: () => 'cursor-pointer text-zinc-500 hover:text-zinc-300',
    group: () => 'py-1',
    groupHeading: () => 'px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500',
    indicatorSeparator: () => 'hidden',
    indicatorsContainer: () => 'gap-1 px-2',
    input: () => 'text-zinc-300',
    loadingIndicator: () => 'text-zinc-500',
    loadingMessage: () => 'px-3 py-2 text-sm text-zinc-500',
    // Grow to fit the longest option (capped, never narrower than the control) so names aren't truncated in a narrow field
    menu: () => 'mt-1 w-max! min-w-full max-w-[min(90vw,36rem)] overflow-hidden rounded-md border border-zinc-700 bg-zinc-900 shadow-lg',
    menuList: () => 'p-1',
    menuPortal: () => 'z-50!',
    multiValue: () => 'items-center rounded border border-zinc-700 bg-zinc-800',
    multiValueLabel: () => 'min-w-0 px-1.5 py-0.5 text-xs text-zinc-200',
    multiValueRemove: () => 'cursor-pointer rounded-r px-1 text-zinc-500 hover:bg-red-500/20 hover:text-red-400',
    noOptionsMessage: () => 'px-3 py-2 text-sm text-zinc-500',
    option: (state) =>
        state.isDisabled
            ? 'cursor-not-allowed rounded px-2 py-1.5 opacity-40'
            : `cursor-pointer rounded px-2 py-1.5 ${state.isSelected ? 'bg-blue-950/40' : state.isFocused ? 'bg-zinc-800' : ''}`,
    placeholder: () => 'text-[#7c8083]',
    singleValue: () => 'text-zinc-300',
    valueContainer: () => 'gap-1 px-3 py-1'
});

/**
 * Generic searchable single/multi select for DB-backed entity lists, styled to
 * match the app form system. Rows show name, optional meta line and badge, and
 * a #id chip; search matches name, id (with or without #), and searchText.
 * Domain wrappers (e.g. OptionBuyingStrategySelect) supply the items.
 */
const SearchSelect = (props: SearchSelectProps) => {
    const {
        items,
        isLoading = false,
        isServerFiltered = false,
        loadError,
        onSearchChange,
        noOptionsMessage,
        resolveMissingItem = defaultMissingItem,
        label,
        'aria-label': ariaLabel,
        helpText,
        error,
        placeholder,
        isDisabled = false,
        size = 'md',
        fullWidth = true,
        noMargin = false,
        className = '',
        id
    } = props;

    const generatedId = useId();
    const inputId = id ?? generatedId;

    const itemsByValue = useMemo(() => new Map(items.map((item) => [item.value, item])), [items]);
    const options = useMemo(() => groupItems(items), [items]);

    const selectedValue = useMemo(() => {
        const resolveItem = (value: number): SearchSelectItem => itemsByValue.get(value) ?? resolveMissingItem(value);

        if (props.mode === 'multi') {
            return props.value.map(resolveItem);
        }

        return props.value === null ? null : resolveItem(props.value);
    }, [props.mode, props.value, itemsByValue, resolveMissingItem]);

    const selectClassNames = useMemo(() => buildClassNames(Boolean(error), size), [error, size]);

    const handleChange = (newValue: MultiValue<SearchSelectItem> | SingleValue<SearchSelectItem>) => {
        if (props.mode === 'multi') {
            props.onChange(Array.isArray(newValue) ? newValue.map((item: SearchSelectItem) => item.value) : []);
        } else {
            props.onChange(!newValue || Array.isArray(newValue) ? null : (newValue as SearchSelectItem).value);
        }
    };

    return (
        <div className={`${getContainerClasses(fullWidth, noMargin)} ${className}`}>
            {label && (
                <label className={formStyles.label} htmlFor={inputId}>
                    {label}
                </label>
            )}
            <Select
                isClearable
                unstyled
                aria-label={ariaLabel}
                classNames={selectClassNames}
                closeMenuOnSelect={props.mode !== 'multi'}
                components={SELECT_COMPONENTS}
                filterOption={isServerFiltered ? (candidate) => candidate.data.serverSearchMatch !== false : filterOption}
                hideSelectedOptions={false}
                inputId={inputId}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isMulti={props.mode === 'multi'}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                noOptionsMessage={() => noOptionsMessage ?? 'No matching options'}
                options={options}
                placeholder={placeholder ?? (props.mode === 'multi' ? 'Select options...' : 'Select an option...')}
                value={selectedValue}
                onChange={handleChange}
                onInputChange={(value, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                        onSearchChange?.(value);
                    } else if (actionMeta.action === 'set-value' || actionMeta.action === 'menu-close') {
                        onSearchChange?.('');
                    }

                    return value;
                }}
            />
            {helpText && <div className={formStyles.helpText}>{helpText}</div>}
            {error && <div className={formStyles.errorText}>{error}</div>}
            {loadError && <div className={formStyles.errorText}>{loadError}</div>}
        </div>
    );
};

export default SearchSelect;
